import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Overline, Reveal } from "@/lib/reveal";
import coverOne from "@/assets/papers-cover-one.jpg";
import coverTwo from "@/assets/papers-cover-two.jpg";

const ISSUES = [
  {
    n: "One",
    title: "The Stories We Carry",
    date: "July 2026",
    href: "/papers/stories-we-carry",
    cover: coverOne,
    coverAlt: "Cover of Issue One — two figures walking a windswept shoreline under a vast clouded sky",
    testid: "papers-issue-01-link",
  },
  {
    n: "Two",
    title: "The Healing Power of Journaling",
    date: "September 2026",
    href: "/papers/healing-power-of-journaling",
    cover: coverTwo,
    coverAlt: "Cover of Issue Two — a woman seated in dune grass at dawn with a notebook in her lap",
    testid: "papers-issue-02-link",
  },
];

export function Papers() {
  return (
    <section
      id="papers"
      data-testid="papers-section"
      aria-labelledby="papers-heading"
      className="relative border-y border-lightline bg-softivory py-24 text-lightink sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <Reveal>
          <Overline index="05" label="The Inkwell Papers" light />
        </Reveal>
        <div className="mt-8 flex flex-wrap items-end justify-between gap-6">
          <Reveal delay={0.08}>
            <h2
              id="papers-heading"
              className="max-w-2xl font-serif text-3xl font-light leading-tight tracking-tight sm:text-5xl"
            >
              Reading for
              <em className="italic text-lightaccent"> the inner life.</em>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="max-w-sm text-sm leading-relaxed text-lightink/65">
              Writing on memory, reflection, and the stories that shape a life.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="mt-14">
          <ul className="border-t border-lightline">
            {ISSUES.map((issue) => (
              <li key={issue.n} className="border-b border-lightline">
                <Link
                  to={issue.href}
                  data-testid={issue.testid}
                  aria-label={`Read Issue ${issue.n}: ${issue.title}`}
                  className="group grid grid-cols-[auto_auto_1fr_auto] items-center gap-5 py-8 transition-colors duration-500 hover:bg-white/40 sm:gap-10"
                >
                  <span className="film-frame hidden sm:block">
                    <img
                      src={issue.cover}
                      alt={issue.coverAlt}
                      loading="lazy"
                      className="h-32 w-24 object-cover"
                    />
                  </span>
                  <span className="font-mono text-xs tracking-[0.25em] text-lightaccent">
                    Issue {issue.n}
                  </span>
                  <span>
                    <span className="block font-serif text-2xl font-light italic transition-all duration-500 group-hover:translate-x-2 group-hover:text-lightaccent sm:text-4xl">
                      {issue.title}
                    </span>
                    <span className="mt-2 block font-mono text-[11px] tracking-[0.25em] uppercase text-lightink/55">
                      {issue.date}
                    </span>
                  </span>
                  <span className="flex items-center gap-3 font-mono text-[11px] tracking-[0.22em] uppercase text-lightink/60 transition-colors duration-300 group-hover:text-lightaccent">
                    <span className="hidden sm:inline">Read 6 pages</span>
                    <ArrowUpRight
                      className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1"
                      aria-hidden="true"
                    />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
}
